/**
 * The shared rainvic-tool roster as a checkbox list — "who owns this?".
 *
 * Extracted from AssignStatusDialog when the F-126 BGM review needed the same
 * question asked a second time. Every action that materializes a task on the
 * rainvic board has to name an owner, so this will keep being asked; one
 * component means the roster loads, fails and renders identically each time,
 * including the two failure modes that are easy to get wrong (a server with no
 * task bridge, and a roster that is simply empty).
 */
import type { TeamMember } from "@kola/shared";
import { useEffect, useState } from "react";

import { ApiError, fetchTeam } from "../core/api.js";

export interface RosterPickerProps {
  /** Selected uids — owned by the caller, which also builds the assignees. */
  selected: ReadonlySet<string>;
  onToggle: (uid: string) => void;
  /** Loaded roster, hoisted so the caller can map uids → display names. */
  onLoaded: (team: TeamMember[]) => void;
  disabled?: boolean;
}

export function RosterPicker({ selected, onToggle, onLoaded, disabled }: RosterPickerProps) {
  const [team, setTeam] = useState<TeamMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchTeam()
      .then((members) => {
        if (cancelled) return;
        setTeam(members);
        onLoaded(members);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError && err.code === "team-not-configured"
            ? "The team roster is not available on this server."
            : "Could not load the team roster. Try again.",
        );
        setTeam([]);
        onLoaded([]);
      });
    return () => {
      cancelled = true;
    };
    // Deliberately once per mount. `onLoaded` is usually an inline closure, so
    // depending on it would refetch the whole roster on every parent render.
  }, []);

  if (team === null) return <div className="empty-state">Loading team…</div>;
  if (error !== null) return <div className="error-note">{error}</div>;
  if (team.length === 0) {
    return (
      <div className="empty-state">No teammates found — they appear after signing in once.</div>
    );
  }

  return (
    <ul className="assign-roster">
      {team.map((member) => (
        <li key={member.uid}>
          <label className="assign-member">
            <input
              type="checkbox"
              checked={selected.has(member.uid)}
              disabled={disabled === true}
              onChange={() => onToggle(member.uid)}
            />
            {member.avatarUrl ? (
              <img className="assign-avatar" src={member.avatarUrl} alt="" />
            ) : (
              <span className="assign-avatar assign-avatar-fallback" aria-hidden>
                {initials(member.displayName)}
              </span>
            )}
            <span className="assign-name">{member.displayName}</span>
            <span className="assign-email">{member.email}</span>
          </label>
        </li>
      ))}
    </ul>
  );
}

/** Assignee DTOs for the uids the caller has selected. */
export function assigneesFrom(
  team: readonly TeamMember[],
  selected: ReadonlySet<string>,
): Array<{ uid: string; name: string | null }> {
  return team
    .filter((member) => selected.has(member.uid))
    .map((member) => ({ uid: member.uid, name: member.displayName || null }));
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]![0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]![0] ?? "") : "";
  return (first + last).toUpperCase();
}
